import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Spin, Divider, Collapse, Table, Tag } from "antd";
import axios from "axios";
import { HEADERS_EXPORT_BEN, POST_LAWSUIT_DETAIL } from "../../../API/apiUrls";
import DateCustom from "../../../../hook/DateCustom";

// ========================================
// Main Component
// ========================================

const DetailWcc = ({ open, close, dataDefault }) => {
  const [convertDateThai] = DateCustom();

  // States
  const [loading, setLoading] = useState(false);
  const [dataWcc, setDataWcc] = useState([]); // ✅ เปลี่ยนเป็น dataWcc

  // ========================================
  // ✅ Load WCC Data
  // ========================================

  useEffect(() => {
    if (!open || !dataDefault) return;

    const loadWccData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          POST_LAWSUIT_DETAIL,
          {
            CONTNO: dataDefault.CONTNO,
            loanId: dataDefault.loanId,
            lawsuitId: dataDefault.lawsuitId,
          },
          {
            headers: HEADERS_EXPORT_BEN,
          },
        );

        if (response?.data?.wcc) {
          setDataWcc(response.data.wcc);
          console.log("✅ WCC data loaded:", response.data.wcc);
        }
      } catch (error) {
        console.error("❌ Error loading WCC data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadWccData();
  }, [open, dataDefault]);

  // ========================================
  // ✅ Table Columns สำหรับ WCC
  // ========================================

  const wccColumns = useMemo(
    () => [
      {
        title: "เลขพัสดุส่ง",
        dataIndex: "parcelNo",
        key: "parcelNo",
        width: 150,
      },
      {
        title: "เลขพัสดุตอบกลับ",
        dataIndex: "parcelNoResponse",
        key: "parcelNoResponse",
        width: 150,
      },
      {
        title: "วันที่ตอบกลับ",
        dataIndex: "dateResponse",
        key: "dateResponse",
        width: 150,
        render: (date) => (date ? convertDateThai(date) : "-"),
      },
      {
        title: "สถานะ",
        dataIndex: "status",
        key: "status",
        width: 120,
        align: "center",
        render: (status) => {
          const statusConfig = {
            1: { text: "ใบตอบกลับ", color: "green" },
            2: { text: "เว็บไปรษณีย์", color: "blue" },
            3: { text: "ตีกลับ", color: "orange" },
            0: { text: "ยังไม่ตอบ", color: "red" },
          };
          const config = statusConfig[status] || statusConfig[0];
          return <Tag color={config.color}>{config.text}</Tag>;
        },
      },
      {
        title: "รูปภาพ",
        dataIndex: "image",
        key: "image",
        width: 120,
        align: "center",
        render: () => "-", // ✅ เพิ่มในภายหลังถ้ามีรูปภาพ
      },
    ],
    [convertDateThai],
  );

  // ========================================
  // ✅ Render รายละเอียด WCC
  // ========================================

  const renderWccDetail = useMemo(() => {
    if (!dataWcc || dataWcc.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            border: "1px solid #e8e8e8",
            borderRadius: "8px",
            color: "#999",
          }}
        >
          ไม่มีข้อมูลหนังสือบอกเลิก
        </div>
      );
    }

    // ✅ จัดกลุ่ม WCC ตามคน
    const groupedWcc = dataWcc.reduce((acc, item) => {
      const key = `${item.customerFullname}_${item.customerTypeID}`;
      if (!acc[key]) {
        acc[key] = {
          customerFullname: item.customerFullname,
          customerTypeID: item.customerTypeID,
          items: [],
        };
      }
      acc[key].items.push(item);
      return acc;
    }, {});

    const wccItems = Object.values(groupedWcc).map((group, index) => {
      const hasResponse = group.items.some((item) => item.status === 1);
      const allPending = group.items.every((item) => item.status === 0);

      return {
        key: String(index),
        label: (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>
              <strong>{group.customerFullname}</strong>
              <Tag
                color={group.customerTypeID === 0 ? "blue" : "orange"}
                style={{ marginLeft: 8 }}
              >
                {group.customerTypeID === 0
                  ? "ผู้เช่าซื้อ"
                  : `คนค้ำที่ ${group.customerTypeID}`}
              </Tag>
            </span>
            <span>
              <Tag
                color={hasResponse ? "green" : allPending ? "red" : "orange"}
              >
                {hasResponse
                  ? "มีตอบกลับ"
                  : allPending
                    ? "ยังไม่ตอบทั้งหมด"
                    : "ตอบบางส่วน"}
              </Tag>
            </span>
          </div>
        ),
        children: (
          <Table
            dataSource={group.items}
            columns={wccColumns}
            rowKey={(record) => `${record.id}_${record.parcelNo}`}
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 800 }}
          />
        ),
      };
    });

    return (
      <>
        <Divider>รายละเอียดหนังสือบอกเลิก</Divider>
        <Collapse items={wccItems} />
      </>
    );
  }, [dataWcc, wccColumns]);

  // ========================================
  // Main Render
  // ========================================

  return (
    <Modal
      title={`หนังสือบอกเลิก - ${dataDefault?.CONTNO || ""}`}
      open={open}
      onCancel={close}
      width={1200}
      footer={null}
      destroyOnHidden
    >
      <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
        {renderWccDetail}
      </Spin>
    </Modal>
  );
};

export default DetailWcc;
